import Vue from 'vue';
import dayjs from 'dayjs';
import { createAuth0Client } from '@auth0/auth0-spa-js';
import store from '@/store';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/**
 * Creates an instance of the Auth0 SDK.
 * If one has already been created, it returns that instance.
 */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...options
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
      };
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
      },
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();

          this.isAuthenticated = true;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(o) {
        return this.auth0Client.getIdTokenClaims(o);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },
      /** Gets the access token using a popup window */

      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      },
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      const { pathname, search } = window.location;

      // Sweep stale auth0-spa-js transaction cookies (a0.spajs.txs.<state>).
      // loginWithRedirect() writes one per login transaction; it is removed on
      // the matching callback. Redirects whose callback never ran — abandoned
      // logins and Auth0 error redirects (?error=&state=, no code) — left
      // orphans that accumulated on this domain. Enough of them (~53 at ~376B
      // each) push the request header past CloudFront's ~20KB limit, which
      // returns a 494 on the /login callback and wedges the user out entirely.
      // Retained across the refresh-token migration as a one-time cleanup of
      // residual v1 orphans already sitting in users' browsers.
      //
      // Only sweep on a non-callback load: a real pending transaction exists
      // only mid-redirect (which always carries code & state), so any txs
      // cookie present here is stale and safe to delete. Scoped to the
      // a0.spajs.txs. prefix — session, app, and token-cache cookies are left
      // untouched. URLSearchParams (not substring) so a value like
      // ?filter=state=active is not misread as a callback. (CS-1929)
      const params = new URLSearchParams(search);
      if (!params.has('code') && !params.has('state')) {
        document.cookie.split(';').forEach((cookie) => {
          const name = cookie.split('=')[0].trim();
          if (name.startsWith('a0.spajs.txs.')) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        });
      }

      // Create a new instance of the SDK client using members of the given options object.
      //
      // Refresh tokens (useRefreshTokens) make getTokenSilently() renew via the
      // /oauth/token refresh_token grant instead of a prompt=none iframe. The
      // grant uses protocol `oauth2-refresh-token`, which the Require-MFA post-
      // login Action skips — so renewal never triggers an MFA challenge that an
      // iframe cannot complete. cacheLocation:'localstorage' persists the refresh
      // token across reloads; offline_access scope is required to be issued one.
      // useRefreshTokensFallback:false deliberately disables the iframe fallback
      // so a missing/expired refresh token becomes a full login (which CAN do
      // MFA) rather than re-introducing the silent-auth MFA bounce.
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
        useRefreshTokensFallback: false,
        authorizationParams: {
          redirect_uri: options.redirectUri,
          audience: options.audience,
          scope: 'openid profile email offline_access',
        },
      });

      const isAuthCallback =
        search.includes('code=') && search.includes('state=');

      // Fast path: on a normal load/refresh where we still hold a valid,
      // non-expired access token, trust it and skip silent auth entirely.
      // Otherwise the prompt=none silent-auth iframe hits the Require-MFA
      // Action, fails with `login_required` (the iframe cannot complete an MFA
      // challenge), and bounces the user to the login screen on EVERY refresh.
      // The stored access token is authoritative for API calls; when it
      // expires, the axios 401 interceptor and the router expiry guard renew it
      // (or redirect) — so we only need to talk to Auth0 when there is no
      // usable token, or when we are consuming an auth code from a redirect.
      const hasValidToken =
        !!store.getters.authToken &&
        !!store.getters.authExpiry &&
        dayjs().isBefore(dayjs(store.getters.authExpiry));

      if (!isAuthCallback && hasValidToken) {
        this.isAuthenticated = true;
        this.loading = false;
        return;
      }

      let redirecting = false;

      try {
        // Handle redirect callback FIRST so the auth code from the URL is
        // consumed (and the refresh token stored) before any renewal attempt.
        if (isAuthCallback) {
          const { appState } = await this.auth0Client.handleRedirectCallback();
          onRedirectCallback(appState);
        }

        const token = await this.auth0Client.getTokenSilently();
        await store.dispatch('saveToken', {
          token,
          expiresIn: 86400,
        });
      } catch (e) {
        this.error = e;
        redirecting = true;

        if (pathname !== '/' && pathname !== '/login' && !search.includes('code=')) {
          sessionStorage.setItem('authTargetUrl', pathname + search);
        }

        // Use loginWithRedirect (not buildAuthorizeUrl) so the SDK persists
        // the PKCE transaction and the user is taken through the full login
        // UI, including MFA prompts.
        try {
          await this.auth0Client.loginWithRedirect({
            authorizationParams: { redirect_uri: options.redirectUri },
          });
        } catch (redirectError) {
          // If the redirect itself fails (e.g. network error building the
          // authorize URL), don't leave the app stuck on the loading spinner
          // forever — fall through to state init so loading is cleared. The
          // error is stored on the instance for debugging (note: no component
          // currently renders $auth.error, so this does not yet show a message).
          redirecting = false;
          this.error = redirectError;
        }
      } finally {
        // Skip state init when redirecting away — otherwise we briefly flash
        // isAuthenticated=false / loading=false on the unmounting page, which
        // can trip Vue watchers and router guards before navigation completes.
        if (!redirecting) {
          this.isAuthenticated = await this.auth0Client.isAuthenticated();
          this.user = await this.auth0Client.getUser();
          this.loading = false;
        }
      }
    },
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(VueInstance, options) {
    VueInstance.prototype.$auth = useAuth0(options);
  },
};
